import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router";
import { ProblemType } from "@probetalab/contracts";
import { ApiError } from "../lib/api.js";
import { useSession } from "../lib/session.js";
import { Button, Field, Input } from "../components/ui.js";
import { Icon, Mark } from "../components/icons.js";
import { applyLabBranding, loginIdentity, readRememberedLab, rememberLabAddress, safeOrigin } from "../lib/labBranding.js";
import { useSiteBranding } from "../lib/site.js";

/** A laboratory and the address its staff sign in at. */
interface LabAddress {
  name: string;
  logo: string | null;
  origin: string;
}

const hostOf = (origin: string) => new URL(origin).host;

export function LoginPage() {
  const { status, login } = useSession();
  const location = useLocation();
  const site = useSiteBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The platform host refused this laboratory's staff (ADR 0025): show where they sign in instead.
  const [refused, setRefused] = useState<LabAddress | null>(null);
  const [remembered, setRemembered] = useState(readRememberedLab);

  // The server's branding arrives after the first paint: retitle the signed-out tab with it.
  useEffect(() => {
    if (status === "anonymous") applyLabBranding(null);
  }, [site, status]);

  if (status === "ready") {
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    // Inicio is the landing after sign-in; a deep link (a capture, an order) is kept.
    return <Navigate to={from === "/iniciar-sesion" || from === "/" ? "/inicio" : from} replace />;
  }

  // On the platform host, a device that belongs to a laboratory with its own address is pointed there.
  const onPlatform = site?.host_kind === "platform";
  const moved: LabAddress | null =
    onPlatform && remembered?.origin && hostOf(remembered.origin) !== window.location.host
      ? { name: remembered.name, logo: remembered.logo, origin: remembered.origin }
      : null;
  const identity = moved ? null : loginIdentity();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email.trim(), password);
    } catch (err) {
      const origin = err instanceof ApiError ? safeOrigin(err.problem.primary_origin) : null;
      if (err instanceof ApiError && err.problem.type === ProblemType.platform_login_disabled && origin) {
        const name = err.problem.lab_display_name ?? "Tu laboratorio";
        rememberLabAddress(name, origin);
        const known = readRememberedLab();
        setRemembered(known);
        setRefused({ name, logo: known?.logo ?? null, origin });
        setPassword("");
      } else if (err instanceof ApiError && err.problem.status === 401) setError("Correo o contraseña incorrectos.");
      else if (err instanceof ApiError) setError(err.problem.detail ?? err.problem.title);
      else setError("No se pudo conectar con el servidor. Revisa la conexión.");
    } finally {
      setBusy(false);
    }
  }

  if (refused) {
    return (
      <div className="screen-center brand">
        <div className="login">
          <LabAddressCard lab={refused} platformHost={site?.platform_host ?? window.location.host} />
          <Button variant="ghost" icon="chevronLeft" onClick={() => setRefused(null)}>
            Volver
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen-center brand">
      <form className="login" onSubmit={onSubmit} noValidate>
        {identity ? (
          <div className="login-lab">
            {identity.logo && <img src={identity.logo} alt="" className="login-lab-logo" />}
            <span className="login-lab-name">{identity.name}</span>
            {/* The product's by-line links to its site (Abraham, 2026-11-03); new tab, so the login stays. */}
            <a className="login-lab-by" href="https://probetalab.com" target="_blank" rel="noopener noreferrer" title="Conoce Probeta Lab">
              <Mark /> Probeta Lab
            </a>
          </div>
        ) : (
          <div className="brand">
            <Mark />
            <span className="text">
              <a className="name login-product-link" href="https://probetalab.com" target="_blank" rel="noopener noreferrer" title="Conoce Probeta Lab">
                Probeta Lab
              </a>
              <br />
              <span className="sub">{onPlatform ? "Consola de soporte" : "Sistema del laboratorio"}</span>
            </span>
          </div>
        )}
        {moved && <LabAddressCard lab={moved} compact />}
        <div>
          <h1 className="h1">Iniciar sesión</h1>
          <p className="muted">{moved ? "Aquí inicia sesión el soporte de Probeta Lab." : "Usa la cuenta que te dio el laboratorio."}</p>
        </div>
        <Field label="Correo">
          <Input type="email" inputMode="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus={!moved} className="lg" placeholder="nombre@laboratorio.mx" />
        </Field>
        <Field label="Contraseña" error={error}>
          <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="lg" aria-invalid={error ? true : undefined} />
        </Field>
        <Button type="submit" variant={moved ? "secondary" : "primary"} size="lg" block busy={busy} disabled={!email || !password}>
          Iniciar sesión
        </Button>
        <p className="login-hint">Si olvidaste tu contraseña, pídele al administrador del laboratorio que la restablezca.</p>
      </form>
    </div>
  );
}

/**
 * "Your laboratory has its own address" (ADR 0025). Full after the platform host refused the lab's
 * staff; compact above the form when this device already knows the address.
 */
export function LabAddressCard({ lab, compact, platformHost }: { lab: LabAddress; compact?: boolean; platformHost?: string }) {
  const host = hostOf(lab.origin);
  const href = `${lab.origin}/iniciar-sesion`;
  if (compact) {
    return (
      <div className="lab-address compact" role="note">
        <div className="lab-address-who">
          {lab.logo ? <img src={lab.logo} alt="" className="lab-address-logo" /> : <Icon name="building" />}
          <div>
            <strong>{lab.name}</strong>
            <span>tiene su propia dirección</span>
          </div>
        </div>
        <a className="btn btn-primary btn-block" href={href}>
          Ir a {host}
          <Icon name="arrowRight" />
        </a>
      </div>
    );
  }
  return (
    <div className="lab-address" role="status" aria-live="polite">
      {lab.logo && <img src={lab.logo} alt="" className="login-lab-logo" />}
      <h1 className="h1">{lab.name} tiene su propia dirección</h1>
      <p className="lab-address-host">
        <span>{host}</span>
      </p>
      <p className="muted">
        Inicia sesión ahí con tu misma cuenta y contraseña.{platformHost ? ` ${platformHost} es solo para el soporte de Probeta Lab.` : ""}
      </p>
      <a className="btn btn-primary btn-lg btn-block" href={href}>
        Ir a {host}
        <Icon name="arrowRight" />
      </a>
      <p className="login-hint">En la tableta, abre la nueva dirección y agrégala a la pantalla de inicio desde el menú del navegador.</p>
    </div>
  );
}
